// src/components/teams/useMyTeams.js
import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase-client';
import { useAuth } from '../../auth/AuthContext';
import { useOnlineRefreshSignal } from '../../utils/onlineStatus';
import { readOfflineContentCache, writeOfflineContentCache } from '../../utils/offlineContentCache';

const CACHE_SCOPE = 'teams';

function getTeamCacheParts(userId) {
  return [userId, CACHE_SCOPE, 'list'];
}

function deriveAdminTeamIds(teams = []) {
  return teams.filter((team) => team.role === 'admin').map((team) => team.id);
}

/**
 * Hook to fetch teams for the current user by reading team_roles from their profile.
 * Avoids full collection queries by batching lookup of known team IDs.
 */
export function useMyTeams() {
  const {
    user
  } = useAuth();
  const [teams, setTeams] = useState([]);
  const [adminTeams, setAdminTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const onlineRefreshSignal = useOnlineRefreshSignal();
  const reloadTeams = useCallback(async () => {
    if (!user) {
      setTeams([]);
      setAdminTeams([]);
      setLoading(false);
      return;
    }
    const cachedTeams = readOfflineContentCache(getTeamCacheParts(user.uid), []);
    if (cachedTeams.length > 0) {
      setTeams(cachedTeams);
      setAdminTeams(deriveAdminTeamIds(cachedTeams));
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      // 1) Read user's profile for team_roles map
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        if (!userSnap.metadata?.fromCache) {
          setTeams([]);
          setAdminTeams([]);
          writeOfflineContentCache(getTeamCacheParts(user.uid), []);
        }
        return;
      }
      const {
        team_roles: roles = {}
      } = userSnap.data();
      const teamIds = Object.keys(roles);
      if (teamIds.length === 0) {
        if (!userSnap.metadata?.fromCache) {
          setTeams([]);
          setAdminTeams([]);
          writeOfflineContentCache(getTeamCacheParts(user.uid), []);
        }
        return;
      }

      // 2) Batch fetch each referenced team document
      const fetches = teamIds.map(id => getDoc(doc(db, 'teams', id)).then(snap => ({
        snap,
        id
      })));
      const results = await Promise.all(fetches);
      const allTeamResultsFromCache = results.every(({ snap }) => snap.metadata?.fromCache);

      // 3) Build teams array with role attached
      const fetched = results.filter(({
        snap
      }) => snap.exists()).map(({
        snap,
        id
      }) => {
        const data = snap.data();
        return {
          id,
          ...data,
          role: roles[id]
        };
      });
      if (fetched.length === 0 && cachedTeams.length > 0 && (userSnap.metadata?.fromCache || allTeamResultsFromCache)) {
        return;
      }
      setTeams(fetched);
      setAdminTeams(deriveAdminTeamIds(fetched));
      if (fetched.length > 0 || !userSnap.metadata?.fromCache) {
        writeOfflineContentCache(getTeamCacheParts(user.uid), fetched);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [user]);
  useEffect(() => {
    reloadTeams();
  }, [onlineRefreshSignal, reloadTeams]);
  return {
    teams,
    adminTeams,
    loading,
    error,
    reloadTeams
  };
}
